import {
  EXPORT_LEGEND_OPTIONS,
  LEGEND_CHECKBOX_CLASS,
  LEGEND_ITEM_CHECKED_CLASS,
  LEGEND_ITEM_CLASS,
  LEGEND_SWATCH_CLASS,
} from 'App/dashboards/_utils/buildLegendCheckbox'
import { downloadSvgAsJpg } from 'App/dashboards/_utils/dashboardDownloads'
import { Highcharts } from 'components/HighchartsChart/_utils/highcharts-init'
import type { Options, StockChart } from 'highcharts/highstock'
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { colors } from 'settings/colors'

// Styles the custom legend rows that buildLegendCheckbox renders into Highcharts' useHTML legend:
// [checkbox][color swatch][name]. The swatch uses currentColor (set inline per series). The checkbox
// fills dark + shows a white tick when its row has the checked class (synced to series visibility).
const legendCheckboxCss = {
  // We draw our own swatches, so hide Highcharts' own legend symbols. symbolWidth:0 collapses the
  // line, but the line series' marker still renders as a stray dot at the left — hide it (scoped to
  // the legend so on-chart data markers are untouched).
  '& .highcharts-legend-item .highcharts-point': { display: 'none' },
  '& .highcharts-legend-item .highcharts-graph': { display: 'none' },
  [`& .${LEGEND_ITEM_CLASS}`]: { display: 'inline-flex', alignItems: 'center', gap: 6 },
  [`& .${LEGEND_CHECKBOX_CLASS}`]: {
    position: 'relative' as const,
    boxSizing: 'border-box' as const,
    width: 14,
    height: 14,
    flex: '0 0 auto',
    border: `1.5px solid ${colors.grey500}`,
    borderRadius: 3,
    background: colors.white,
  },
  [`& .${LEGEND_ITEM_CHECKED_CLASS} .${LEGEND_CHECKBOX_CLASS}`]: {
    background: colors.midnight,
    borderColor: colors.midnight,
  },
  [`& .${LEGEND_ITEM_CHECKED_CLASS} .${LEGEND_CHECKBOX_CLASS}::after`]: {
    content: '""',
    position: 'absolute' as const,
    left: 4,
    top: 1,
    width: 3,
    height: 7,
    border: 'solid #fff',
    borderWidth: '0 2px 2px 0',
    transform: 'rotate(45deg)',
  },
  [`& .${LEGEND_SWATCH_CLASS}`]: { flex: '0 0 auto', display: 'inline-block', width: 16 },
  [`& .${LEGEND_SWATCH_CLASS}--line`]: { height: 3, borderRadius: 2, background: 'currentColor' },
  [`& .${LEGEND_SWATCH_CLASS}--dash`]: { height: 0, borderTop: '2px dashed currentColor' },
  [`& .${LEGEND_SWATCH_CLASS}--square`]: {
    width: 14,
    height: 14,
    borderRadius: 2,
    background: 'currentColor',
  },
}

export type TDashboardChartHandle = { downloadImage: (filename: string) => void }

export const DashboardChart = forwardRef<
  TDashboardChartHandle,
  {
    id?: string
    className?: string
    chartOptions: Options
  }
>(({ chartOptions, id, className }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstanceRef = useRef<StockChart | null>(null)
  // Keep the latest options in a ref so the imperative handle (download) can read them without being
  // recreated on every options change — same ref-based access as the chart instance/container above.
  const chartOptionsRef = useRef(chartOptions)

  chartOptionsRef.current = chartOptions

  // Expose a JPG download that rasterizes the chart's live SVG (no Highcharts exporting module).
  useImperativeHandle(
    ref,
    () => ({
      downloadImage: (filename: string) => {
        const chart = chartInstanceRef.current
        const node = chartRef.current
        const options = chartOptionsRef.current

        if (!chart || !node) return

        // The export captures the SVG only; a useHTML legend lives in an HTML overlay and wouldn't be
        // included. Swap to a plain SVG-native legend for the snapshot, then revert. downloadSvgAsJpg
        // clones + serializes synchronously, so reverting right after is safe.
        const usesHtmlLegend = Boolean((options.legend as { useHTML?: boolean } | undefined)?.useHTML)

        if (usesHtmlLegend) chart.update({ legend: EXPORT_LEGEND_OPTIONS }, true, false)

        try {
          const svg = node.querySelector('svg')

          if (svg) downloadSvgAsJpg(svg as SVGSVGElement, filename)
        } finally {
          // Always revert, even if rasterizing throws, so the chart never stays on the export legend.
          if (usesHtmlLegend) chart.update({ legend: options.legend }, true, false)
        }
      },
    }),
    [],
  )

  useEffect(() => {
    if (!chartRef.current) return

    if (!chartInstanceRef.current) {
      Highcharts.setOptions({ lang: { thousandsSep: ',' } })

      chartInstanceRef.current = Highcharts.stockChart(chartRef.current, chartOptions)
    } else {
      chartInstanceRef.current.update(chartOptions, true, true)
    }
  }, [chartOptions])

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()

        chartInstanceRef.current = null
      }
    }
  }, [])

  return <div className={className} css={{ width: '100%', ...legendCheckboxCss }} id={id} ref={chartRef} />
})

DashboardChart.displayName = 'DashboardChart'
